<template>
  <div class="position-map-container">
    <!-- Map Container -->
    <div ref="mapContainer" class="map-view"></div>

    <!-- Control Panel -->
    <div class="control-panel">
      <!-- Start/Stop Button -->
      <button 
        @click="toggleTracking"
        :class="['control-btn', 'primary-btn', { active: isTracking }]"
        :disabled="isLoading"
      >
        <span v-if="!isTracking">Start Navigation</span>
        <span v-else>Stop</span>
      </button>

      <!-- Floor Selector -->
      <div class="floor-selector">
        <button 
          @click="changeFloor(-1)" 
          class="control-btn"
          :disabled="!isTracking"
        >
          ↓
        </button>
        <span class="floor-label">Floor {{ currentFloor }}</span>
        <button 
          @click="changeFloor(1)" 
          class="control-btn"
          :disabled="!isTracking"
        >
          ↑
        </button>
      </div>

      <!-- Center on User -->
      <button 
        @click="centerOnUser"
        class="control-btn"
        :disabled="!userPosition || !isTracking"
      >
        📍 Center
      </button>

      <!-- Calibrate Floor -->
      <button 
        @click="calibrateCurrentFloor"
        class="control-btn"
        :disabled="!isTracking"
      >
        📏 Calibrate
      </button>
      
      <!-- Toggle Signal View -->
      <button 
        @click="toggleSignalView"
        class="control-btn"
        :disabled="!isTracking"
      >
        📡 {{ showSignals ? 'Hide' : 'Show' }} Signals
      </button>
    </div>

    <!-- Enhanced Status Panel -->
    <div class="status-panel" v-if="isTracking">
      <div class="status-header">
        <span class="status-title">{{ isIndoor ? '🏠 Indoor' : '🌍 Outdoor' }}</span>
        <span :class="['accuracy-badge', accuracyClass]">
          ±{{ accuracy.toFixed(1) }}m
        </span>
      </div>
      
      <div class="status-grid">
        <div class="status-item">
          <span class="label">Heading</span>
          <span class="value">{{ Math.round(heading) }}°</span>
        </div>
        <div class="status-item">
          <span class="label">Speed</span>
          <span class="value">{{ (speed * 3.6).toFixed(1) }} km/h</span>
        </div>
        <div class="status-item">
          <span class="label">Steps</span>
          <span class="value">{{ stepCount }}</span>
        </div>
        <div class="status-item">
          <span class="label">Distance</span>
          <span class="value">{{ totalDistance.toFixed(0) }}m</span>
        </div>
      </div>
      
      <!-- Signal Strength Indicators -->
      <div class="signal-indicators">
        <div class="signal-bar">
          <span class="signal-label">WiFi</span>
          <div class="signal-meter">
            <div class="signal-fill wifi" :style="`width: ${wifiStrength}%`"></div>
          </div>
          <span class="signal-count">{{ wifiCount }}</span>
        </div>
        <div class="signal-bar">
          <span class="signal-label">BLE</span>
          <div class="signal-meter">
            <div class="signal-fill ble" :style="`width: ${bleStrength}%`"></div>
          </div>
          <span class="signal-count">{{ bleCount }}</span>
        </div>
        <div class="signal-bar" v-if="cellCount > 0">
          <span class="signal-label">Cell</span>
          <div class="signal-meter">
            <div class="signal-fill cell" :style="`width: ${cellStrength}%`"></div>
          </div>
          <span class="signal-count">{{ cellCount }}</span>
        </div>
      </div>
    </div>
    
    <!-- Signal Details Panel -->
    <div class="signal-details" v-if="showSignals && nearbySignals.length > 0">
      <h4>Nearby Signals</h4>
      <div class="signal-list">
        <div v-for="signal in nearbySignals" :key="signal.id" class="signal-item">
          <span :class="['signal-type', signal.type]">{{ signal.type.toUpperCase() }}</span>
          <span class="signal-id">{{ signal.id.substring(0, 8) }}...</span>
          <span class="signal-rssi">{{ signal.rssi }}dBm</span>
          <span class="signal-distance">{{ signal.distance.toFixed(1) }}m</span>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner"></div>
        <p>{{ loadingMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed, defineExpose, watch ,defineEmits} from 'vue'
import { registerPlugin } from '@capacitor/core'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Register the plugin
const LocationPro = registerPlugin('LocationPro')

// Emits
const emit = defineEmits(['position-update', 'positioning-started', 'positioning-stopped'])

// Map references
const mapContainer = ref(null)
let map = null
let userMarker = null
let accuracyCircle = null
let headingLine = null
let pathLine = null
let signalMarkers = []

// State
const isTracking = ref(false)
const isLoading = ref(false)
const loadingMessage = ref('Initializing...')

// Position data
const userPosition = ref(null)
const heading = ref(0)
const currentFloor = ref(0)
const accuracy = ref(10)
const speed = ref(0)
const isMoving = ref(false)
const isIndoor = ref(false)
const indoorConfidence = ref(0)
const stepCount = ref(0)
const totalDistance = ref(0)

// Signal data
const wifiCount = ref(0)
const bleCount = ref(0)
const cellCount = ref(0)
const nearbySignals = ref([])
const showSignals = ref(false)

// Path tracking
const positionHistory = []
const maxHistorySize = 200

// Map state
let mapInitialized = false
let isFirstPosition = true
let followUser = true

// Event listeners
let positionListener = null
let floorListener = null

// Configuration
const config = {
  defaultCenter: [37.7749, -122.4194], // San Francisco
  defaultZoom: 19, // Higher zoom for indoor
  maxZoom: 22,
  minZoom: 15,
  smoothingEnabled: true,
  animationDuration: 150 // Faster animations
}

// Computed properties
const accuracyClass = computed(() => {
  if (accuracy.value <= 2) return 'perfect'
  if (accuracy.value <= 5) return 'excellent'
  if (accuracy.value <= 10) return 'good'
  return 'fair'
})

const wifiStrength = computed(() => {
  return Math.min(100, wifiCount.value * 10)
})

const bleStrength = computed(() => {
  return Math.min(100, bleCount.value * 15)
})

const cellStrength = computed(() => {
  return Math.min(100, cellCount.value * 25)
})

// Initialize map
const initializeMap = async () => {
  await nextTick()
  
  if (!mapContainer.value) {
    console.error('Map container not found')
    return
  }

  // Wait a bit more to ensure container is rendered
  await new Promise(resolve => setTimeout(resolve, 100))

  try {
    // Create map instance with better defaults
    map = L.map(mapContainer.value, {
      center: config.defaultCenter,
      zoom: config.defaultZoom,
      maxZoom: config.maxZoom,
      minZoom: config.minZoom,
      zoomControl: true,
      attributionControl: false,
      worldCopyJump: true, // Prevent map wrapping
      preferCanvas: true // Better performance
    })

    // Add high-quality tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: config.maxZoom,
      maxNativeZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map)

    // Custom user marker with better visibility
    const userIcon = L.divIcon({
      className: 'user-marker',
      html: `
        <div class="marker-container">
          <div class="marker-dot"></div>
          <div class="marker-pulse"></div>
          <div class="marker-arrow"></div>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })

    userMarker = L.marker(config.defaultCenter, {
      icon: userIcon,
      zIndexOffset: 1000,
      rotationAngle: 0
    }).addTo(map)

    // Accuracy circle with gradient
    accuracyCircle = L.circle(config.defaultCenter, {
      radius: 10,
      color: '#4285F4',
      fillColor: '#4285F4',
      fillOpacity: 0.1,
      weight: 2,
      interactive: false
    }).addTo(map)

    // Heading line
    headingLine = L.polyline([], {
      color: '#4285F4',
      weight: 4,
      opacity: 0.7,
      dashArray: '10, 5',
      interactive: false
    }).addTo(map)

    // Path line
    pathLine = L.polyline([], {
      color: '#FF5722',
      weight: 3,
      opacity: 0.5,
      smoothFactor: 1,
      interactive: false
    }).addTo(map)
    
    // Map events
    map.on('dragstart', () => {
      followUser = false
    })

    mapInitialized = true
    console.log('Map initialized successfully')
  } catch (error) {
    console.error('Failed to initialize map:', error)
  }
}

// Start tracking
const startTracking = async () => {
  isLoading.value = true
  loadingMessage.value = 'Starting high-accuracy positioning...'

  try {
    // Get current position first for immediate centering
    if (navigator.geolocation) {
      loadingMessage.value = 'Getting GPS fix...'
      
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude
          const lon = pos.coords.longitude
          
          // Immediately update position and center map
          userPosition.value = [lat, lon]
          if (map && mapInitialized) {
            map.setView([lat, lon], config.defaultZoom, { animate: false })
            userMarker.setLatLng([lat, lon])
            accuracyCircle.setLatLng([lat, lon])
          }
          
          // Set as origin for indoor positioning
          await LocationPro.setIndoorOrigin({ lat, lon })
        },
        (error) => {
          console.warn('GPS not available, using default position')
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      )
    }

    // Start the plugin
    await LocationPro.start()

    // Set up event listeners
    positionListener = await LocationPro.addListener('position', handlePositionUpdate)
    floorListener = await LocationPro.addListener('floorChange', handleFloorChange)

    isTracking.value = true
    isLoading.value = false
    emit('positioning-started')
    
    // Request status update
    const status = await LocationPro.getStatus()
    if (status.lat && status.lon) {
      userPosition.value = [status.lat, status.lon]
      if (map && isFirstPosition) {
        map.setView(userPosition.value, config.defaultZoom)
        isFirstPosition = false
      }
    }
    
  } catch (error) {
    console.error('Failed to start tracking:', error)
    isLoading.value = false
    alert('Failed to start positioning. Please check permissions.')
  }
}

// Stop tracking
const stopTracking = async () => {
  try {
    if (positionListener) {
      positionListener.remove()
      positionListener = null
    }
    if (floorListener) {
      floorListener.remove()
      floorListener = null
    }

    await LocationPro.stop()

    isTracking.value = false
    emit('positioning-stopped')
    
    // Clear path
    positionHistory.length = 0
    if (pathLine) {
      pathLine.setLatLngs([])
    }
    
    // Clear signal markers
    signalMarkers.forEach(marker => map.removeLayer(marker))
    signalMarkers = []
    
  } catch (error) {
    console.error('Failed to stop tracking:', error)
  }
}

// Toggle tracking
const toggleTracking = () => {
  if (isTracking.value) {
    stopTracking()
  } else {
    startTracking()
  }
}

// Handle position updates
const handlePositionUpdate = (data) => {
  // Validate position data
  if (!data || typeof data.lat !== 'number' || typeof data.lon !== 'number' || 
      isNaN(data.lat) || isNaN(data.lon)) {
    return
  }
  
  const newPosition = [data.lat, data.lon]
  
  // Update all state
  userPosition.value = newPosition
  heading.value = data.heading || 0
  accuracy.value = data.accuracy || 10
  speed.value = data.speed || 0
  isMoving.value = data.isMoving || false
  isIndoor.value = data.isIndoor || false
  indoorConfidence.value = data.indoorConfidence || 0
  stepCount.value = data.stepCount || 0
  totalDistance.value = data.totalDistance || 0
  wifiCount.value = data.wifiCount || 0
  bleCount.value = data.bleCount || 0
  cellCount.value = data.cellCount || 0
  nearbySignals.value = data.nearbySignals || []

  // Update map elements
  if (map && mapInitialized) {
    updateMapElements(newPosition, data.heading, data.accuracy)
  }

  // Add to history
  addToHistory(newPosition)
  
  // Auto-center on first position
  if (isFirstPosition && map) {
    map.setView(newPosition, config.defaultZoom, { animate: false })
    isFirstPosition = false
  }
  
  // Emit update event
  emit('position-update', data)
}

// Update map elements smoothly
const updateMapElements = (position, hdg, acc) => {
  if (!map || !userMarker || !mapInitialized) return

  // Update marker position
  const currentPos = userMarker.getLatLng()
  const targetPos = L.latLng(position[0], position[1])
  
  // Only animate if movement is small
  const distance = currentPos.distanceTo(targetPos)
  if (distance < 30) {
    // Smooth animation
    animateMarker(currentPos, targetPos, config.animationDuration)
  } else {
    // Direct update for large movements
    userMarker.setLatLng(targetPos)
  }

  // Update accuracy circle
  if (accuracyCircle) {
    accuracyCircle.setLatLng(targetPos)
    accuracyCircle.setRadius(Math.max(acc, 0.5))
    
    // Dynamic color based on accuracy
    let color = '#4285F4'
    if (acc <= 2) color = '#0F9D58'
    else if (acc <= 5) color = '#4285F4'
    else if (acc <= 10) color = '#FFA500'
    else color = '#FF5722'
    
    accuracyCircle.setStyle({ 
      color, 
      fillColor: color,
      fillOpacity: isIndoor.value ? 0.15 : 0.1
    })
  }

  // Update heading line
  if (headingLine && hdg !== undefined) {
    const headingRad = (hdg * Math.PI) / 180
    const lineLength = Math.max(15, Math.min(50, acc * 2))
    const endLat = position[0] + (lineLength * Math.cos(headingRad)) / 111111
    const endLon = position[1] + (lineLength * Math.sin(headingRad)) / (111111 * Math.cos(position[0] * Math.PI / 180))
    
    headingLine.setLatLngs([position, [endLat, endLon]])
  }

  // Update path line
  if (pathLine && positionHistory.length > 1) {
    pathLine.setLatLngs(positionHistory)
  }

  // Rotate marker arrow
  if (userMarker._icon) {
    const arrow = userMarker._icon.querySelector('.marker-arrow')
    if (arrow) {
      arrow.style.transform = `rotate(${hdg}deg)`
    }
  }
  
  // Auto-follow user if enabled
  if (followUser && map) {
    map.panTo(targetPos, { animate: true, duration: 0.5 })
  }
}

// Smooth marker animation
const animateMarker = (from, to, duration) => {
  const start = Date.now()
  const latDiff = to.lat - from.lat
  const lngDiff = to.lng - from.lng

  const animate = () => {
    const elapsed = Date.now() - start
    const progress = Math.min(elapsed / duration, 1)
    
    // Smooth easing
    const easeProgress = progress < 0.5 
      ? 2 * progress * progress 
      : -1 + (4 - 2 * progress) * progress
    
    const currentLat = from.lat + latDiff * easeProgress
    const currentLng = from.lng + lngDiff * easeProgress
    
    userMarker.setLatLng([currentLat, currentLng])
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    }
  }
  
  requestAnimationFrame(animate)
}

// Add position to history
const addToHistory = (position) => {
  // Only add if position changed significantly
  if (positionHistory.length > 0) {
    const lastPos = positionHistory[positionHistory.length - 1]
    const distance = L.latLng(position[0], position[1]).distanceTo(
      L.latLng(lastPos[0], lastPos[1])
    )
    if (distance < 0.5) return // Less than 0.5m, skip
  }
  
  positionHistory.push(position)
  
  if (positionHistory.length > maxHistorySize) {
    positionHistory.shift()
  }
}

// Handle floor change
const handleFloorChange = (data) => {
  currentFloor.value = data.floor
  console.log(`Floor changed to: ${data.floor}, altitude: ${data.altitude}m`)
}

// Center map on user
const centerOnUser = () => {
  if (map && userPosition.value) {
    map.flyTo(userPosition.value, config.defaultZoom, {
      animate: true,
      duration: 1
    })
    followUser = true
  }
}

// Change floor manually
const changeFloor = (delta) => {
  currentFloor.value += delta
  calibrateCurrentFloor()
}

// Calibrate current floor
const calibrateCurrentFloor = async () => {
  try {
    const result = await LocationPro.calibrateFloor({ 
      floor: currentFloor.value 
    })
    console.log('Floor calibrated:', result)
  } catch (error) {
    console.error('Failed to calibrate floor:', error)
  }
}

// Toggle signal view
const toggleSignalView = () => {
  showSignals.value = !showSignals.value
}

// Expose methods to parent component
defineExpose({
  startPositioning: startTracking,
  stopPositioning: stopTracking,
  forceStopPositioning: stopTracking,
  centerOnUser,
  calibrateFloor: calibrateCurrentFloor,
  getStatus: () => ({
    isTracking: isTracking.value,
    position: userPosition.value,
    floor: currentFloor.value,
    accuracy: accuracy.value,
    heading: heading.value,
    isIndoor: isIndoor.value
  })
})

// Lifecycle
onMounted(async () => {
  await initializeMap()
})

onUnmounted(() => {
  stopTracking()
  
  if (map) {
    map.remove()
    map = null
  }
})
</script>

<style scoped>
.position-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.map-view {
  width: 100%;
  height: 100%;
}

/* Control Panel */
.control-panel {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.control-btn {
  padding: 10px 16px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.control-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #4285F4;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary-btn {
  background: #4285F4;
  color: white;
  border-color: #4285F4;
  min-width: 140px;
}

.primary-btn:hover:not(:disabled) {
  background: #3367D6;
}

.primary-btn.active {
  background: #EA4335;
  border-color: #EA4335;
}

.primary-btn.active:hover {
  background: #D33B2C;
}

/* Floor Selector */
.floor-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 6px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.floor-label {
  font-size: 14px;
  font-weight: 500;
  min-width: 60px;
  text-align: center;
}

/* Enhanced Status Panel */
.status-panel {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  min-width: 240px;
  max-width: 300px;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.status-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.accuracy-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.accuracy-badge.perfect { background: #0F9D58; }
.accuracy-badge.excellent { background: #4285F4; }
.accuracy-badge.good { background: #FFA500; }
.accuracy-badge.fair { background: #FF5722; }

.status-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
}

.status-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px;
  background: #f5f5f5;
  border-radius: 6px;
}

.status-item .label {
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.status-item .value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

/* Signal Indicators */
.signal-indicators {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.signal-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.signal-label {
  font-size: 11px;
  font-weight: 500;
  color: #666;
  width: 30px;
}

.signal-meter {
  flex: 1;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.signal-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.signal-fill.wifi { background: #4285F4; }
.signal-fill.ble { background: #FFA500; }
.signal-fill.cell { background: #9C27B0; }

.signal-count {
  font-size: 11px;
  font-weight: 600;
  color: #333;
  width: 20px;
  text-align: right;
}

/* Signal Details Panel */
.signal-details {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: white;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 999;
  max-width: 320px;
  max-height: 200px;
  overflow-y: auto;
}

.signal-details h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.signal-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.signal-item {
  display: grid;
  grid-template-columns: 40px 1fr 60px 50px;
  gap: 8px;
  align-items: center;
  padding: 4px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 11px;
}

.signal-type {
  font-weight: 600;
  text-align: center;
  padding: 2px 4px;
  border-radius: 3px;
  color: white;
}

.signal-type.wifi { background: #4285F4; }
.signal-type.ble { background: #FFA500; }

.signal-id {
  color: #666;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
}

.signal-rssi {
  color: #333;
  font-weight: 500;
  text-align: right;
}

.signal-distance {
  color: #666;
  text-align: right;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.loading-content {
  text-align: center;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4285F4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhanced Marker Styles */
:deep(.user-marker) {
  background: none !important;
  border: none !important;
}

:deep(.marker-container) {
  position: relative;
  width: 40px;
  height: 40px;
}

:deep(.marker-dot) {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #4285F4, #1976D2);
  border: 3px solid white;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  z-index: 3;
}

:deep(.marker-pulse) {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  background: radial-gradient(circle, rgba(66, 133, 244, 0.4), transparent);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 2s ease-out infinite;
  z-index: 1;
}

:deep(.marker-arrow) {
  position: absolute;
  top: 3px;
  left: 50%;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 12px solid #4285F4;
  transform-origin: bottom center;
  margin-left: -4px;
  z-index: 2;
  transition: transform 0.2s ease;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .control-panel {
    top: 10px;
    right: 10px;
  }
  
  .control-btn {
    padding: 8px 12px;
    font-size: 13px;
  }
  
  .status-panel {
    bottom: 10px;
    left: 10px;
    min-width: 200px;
    padding: 12px;
  }
  
  .signal-details {
    display: none;
  }
}

@media (max-width: 480px) {
  .control-panel {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 150px;
  }
  
  .control-btn {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .primary-btn {
    min-width: 100px;
  }
  
  .floor-selector {
    order: -1;
    width: 100%;
  }
  
  .status-panel {
    min-width: 180px;
    max-width: 220px;
    padding: 10px;
  }
  
  .status-grid {
    grid-template-columns: 1fr;
  }
}
</style>